import React, { useEffect, useState } from 'react';
import { translate } from '../../Services/translation';
import {
  getAddress,
  edit as editAddress,
} from '../../Actions/Address.action';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import Input from '../Inputs/Input';
import AddressViewModel from '../../Types/AddressViewModel';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import Dropdown from '../Dropdown';
import EditorString from '../EditorString/EditorString';
import { EditorStringModel } from '../../Types/EditorString';

const MyPagesEditAddress = ({
  description,
}: {
  description?: EditorStringModel;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [address, setAddress] = useState<AddressViewModel>();
  const [disabled, setDisabled] = useState<boolean>(
    isLoading || !address?.isEditable
  );

  useEffect(() => {
    setDisabled(isLoading || !address?.isEditable);
  }, [isLoading, address?.isEditable]);

  const { id } = useParams();
  const { errors = {} } = useTypedSelector(
    (state) => state.myPage.addresses
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const countriesList = globalThis.LitiumConstants.countries;

  const fetchAddress = async () => {
    const address = await getAddress(id);
    setAddress(address);
    setIsLoading(!isLoading);
  };

  const setAddressValue = (key, value) => {
    const addressCopy = Object.assign({}, address);
    addressCopy[key] = value;
    setAddress(addressCopy);
  };

  const submitForm = (e) => {
    e.preventDefault();
    dispatch(editAddress(address))
      .then(() => navigate(-1))
      .catch(() => {}); // must catch error;
  };

  useEffect(() => {
    fetchAddress();
  }, []);

  return (
    <div className="col-span-3">
      <h1 className="mb-2">{translate('mypage.address.editaddress')}</h1>
      {description && (
        <div className="mb-4 max-w-3xl text-base lg:mb-6">
          <EditorString value={description.value} />
        </div>
      )}

      <form className="flex flex-col" onSubmit={submitForm}>
        <Input
          inputSize="large"
          label={translate('mypage.address.address')}
          wrapperClassName="mb-4 [&_.input]:text-lg"
          value={address?.address}
          onChange={(e) => setAddressValue('address', e.target.value)}
          placeholder={translate('general.placeholder.address')}
          type="text"
          id="address"
          disabled={disabled}
          required={true}
          inputState={errors['address']?.length ? 'error' : 'default'}
          message={
            errors['address']?.length ? errors['address']['0'] : null
          }
        />
        <Input
          inputSize="large"
          label={translate('mypage.address.zipCode')}
          wrapperClassName="mb-4 [&_.input]:text-lg"
          value={address?.zipCode}
          onChange={(e) => setAddressValue('zipCode', e.target.value)}
          placeholder={translate('general.placeholder.zipCode')}
          type="text"
          id="zip"
          disabled={disabled}
          required={true}
          inputState={errors['zipCode']?.length ? 'error' : 'default'}
          message={
            errors['zipCode']?.length ? errors['zipCode']['0'] : null
          }
        />
        <Input
          inputSize="large"
          label={translate('mypage.address.city')}
          wrapperClassName="mb-4 [&_.input]:text-lg"
          onChange={(e) => setAddressValue('city', e.target.value)}
          value={address?.city}
          placeholder={translate('general.placeholder.city')}
          type="text"
          id="city"
          disabled={disabled}
          required={true}
          inputState={errors['city']?.length ? 'error' : 'default'}
          message={errors['city']?.length ? errors['city']['0'] : null}
        />
        <label className="mb-2 text-base font-bold">
          {translate('mypage.address.country')}
        </label>
        <Dropdown
          selectedValue={address?.country}
          className="mb-4 [&_.dropdown-select]:p-3"
          options={countriesList.map((country) => ({
            value: country.value,
            label: country.text,
          }))}
          size="large"
          placeholder={translate('general.placeholder.country')}
          onChange={({ value }) => setAddressValue('country', value)}
          disabled={disabled}
        />
        <Input
          inputSize="large"
          label={translate('mypage.address.phonenumber')}
          wrapperClassName="mb-4 [&_.input]:text-lg"
          value={address?.phoneNumber}
          onChange={(e) => setAddressValue('phoneNumber', e.target.value)}
          placeholder={translate('general.placeholder.phone')}
          type="tel"
          id="tel"
          disabled={disabled}
          required={true}
          inputState={errors['address']?.length ? 'error' : 'default'}
          message={
            errors['address']?.length ? errors['address']['0'] : null
          }
        />
        {address?.isEditable && (
          <div className="flex gap-2">
            <button className="btn btn--primary btn--large" type="submit">
              {translate('general.save')}
            </button>
            <button
              className="btn btn--large btn--clear"
              onClick={(e) => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              {translate('general.cancel')}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};
export default MyPagesEditAddress;
